import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { Banknote, CreditCard, ArrowLeft, CheckCircle2 } from 'lucide-react';

import { Button } from '@/shared/ui/components/button';
import { Forbidden } from '@/modules/auth/Forbidden';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { useVenueContext } from '@/modules/venue/VenueContext';
import { useCompleteSale, useSale, type PaymentMethod } from './sales';

/** Formats a minor-unit amount string as a localized currency display, e.g. "12.50" + "USD" -> "$12.50". */
function formatMoney(amount: string, currency: string): string {
  const value = Number(amount);
  if (Number.isNaN(value)) return `${amount} ${currency}`;
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
}

/**
 * The POS payment screen the End & Pay jump lands on (ADR 0031, PRD #149 stories 19/21). It reads the
 * open check the ended session was handed and settles it with a manual payment (cash or keyed card).
 * Completing records the payment and issues an internal receipt.
 * Gated by `sales.complete`; a viewer without it sees the check but cannot take payment.
 */
export function PosPaymentPage() {
  const { venueId } = useVenueContext();
  const { saleId = '' } = useParams<{ saleId: string }>();
  const navigate = useNavigate();
  const auth = useAuthorization();
  const scope = { kind: 'venue', venueId } as const;
  const canComplete = auth.can('sales.complete', scope);
  const canView = canComplete || auth.can('sales.view', scope);

  const sale = useSale(saleId, canView);
  const complete = useCompleteSale(saleId);

  if (!canView) {
    return <Forbidden />;
  }

  const backToWorkspace = () => navigate(`/loc/${venueId}`);

  const take = async (method: PaymentMethod) => {
    try {
      await complete.mutateAsync(method);
      toast.success('Payment recorded — sale completed');
      backToWorkspace();
    } catch {
      toast.error('Could not complete the sale');
    }
  };

  return (
    <div className="mx-auto max-w-xl space-y-6 p-6">
      <button
        type="button"
        onClick={backToWorkspace}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" /> Back to workspace
      </button>

      <header className="space-y-1">
        <h1 className="text-xl font-semibold">Take payment</h1>
        <p className="text-sm text-muted-foreground">
          Settle the open check from the ended play session as one sale.
        </p>
      </header>

      {sale.isLoading && <p className="text-sm text-muted-foreground">Loading the open check…</p>}
      {sale.isError && <p className="text-sm text-destructive">Could not load this sale.</p>}

      {sale.data && (
        <section className="rounded-lg border">
          <ul className="divide-y">
            {sale.data.lines.map((line) => (
              <li key={line.id} className="flex items-center justify-between px-4 py-3 text-sm">
                <span>{line.label}</span>
                <span className="tabular-nums">{formatMoney(line.amount, line.currency)}</span>
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-between border-t px-4 py-3 font-medium">
            <span>Total</span>
            <span className="tabular-nums">
              {formatMoney(sale.data.totalAmount, sale.data.currency)}
            </span>
          </div>

          {sale.data.state === 'completed' ? (
            <div className="flex items-center gap-2 border-t px-4 py-4 text-sm text-emerald-600">
              <CheckCircle2 className="size-4" /> This sale is already completed.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 border-t p-4">
              <Button
                variant="outline"
                disabled={!canComplete || complete.isPending}
                onClick={() => void take('cash')}
              >
                <Banknote className="size-4" /> Cash
              </Button>
              <Button
                disabled={!canComplete || complete.isPending}
                onClick={() => void take('card_manual')}
              >
                <CreditCard className="size-4" /> Card
              </Button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
